import type { Background, DrawingLabel, OHLCV, SeriesSpec } from "@luxalgo/vela";

export type PatternBarDirection = "long" | "short" | "neutral";

export type PatternBarHighlight = {
  timeMs: number;
  direction?: PatternBarDirection | null;
  /** e.g. "Bullish engulfing" — stacked per word on the chart. */
  label?: string | null;
  /** When false, keep the arrow / tint but skip the text (preview overview). */
  showText?: boolean;
  /** When false, skip the arrow (column wash only). Default: same as showText. */
  showArrow?: boolean;
  /** When false, skip the column wash (selected path: labels only). Default true. */
  showWash?: boolean;
  /** Darker column wash — retest confirm / invalidation. `muted` = other hits. */
  emphasis?: "normal" | "strong" | "muted";
  kind?:
    | "setup"
    | "break"
    | "rejection"
    | "retest"
    | "location_armed"
    | "respect"
    | "reject"
    | "break_retest"
    | "entry"
    | "invalid"
    | "marker";
};

export type PatternBarPalette = {
  solid: string;
  fill: string;
};

/** Long / short use trade colors; inside-bar uses tropical surf. */
export function patternBarPalette(
  direction?: PatternBarDirection | null,
  emphasis: "normal" | "strong" | "muted" = "normal"
): PatternBarPalette {
  const strong = emphasis === "strong";
  const muted = emphasis === "muted";
  if (direction === "short") {
    return {
      solid: muted ? "rgba(224, 1, 1, 0.45)" : "#E00101",
      fill: strong
        ? "rgba(224, 1, 1, 0.42)"
        : muted
          ? "rgba(224, 1, 1, 0.08)"
          : "rgba(224, 1, 1, 0.22)",
    };
  }
  if (direction === "long") {
    return {
      solid: muted ? "rgba(6, 227, 99, 0.45)" : "#06E363",
      fill: strong
        ? "rgba(6, 227, 99, 0.42)"
        : muted
          ? "rgba(6, 227, 99, 0.08)"
          : "rgba(6, 227, 99, 0.22)",
    };
  }
  return {
    solid: muted ? "rgba(56, 187, 209, 0.45)" : "#38BBD1",
    fill: strong
      ? "rgba(56, 187, 209, 0.42)"
      : muted
        ? "rgba(56, 187, 209, 0.1)"
        : "rgba(56, 187, 209, 0.28)",
  };
}

/** Vela may expose seconds; tests use small relative times. */
export function asBarTimeMs(time: number): number {
  if (!Number.isFinite(time)) return time;
  if (time > 1e11) return time;
  if (time > 1e9) return time * 1000;
  return time;
}

export function stackPatternLabelLines(label: string | null | undefined): string[] {
  const text = (label ?? "").trim();
  if (!text) return ["Pattern"];
  if (
    text === "Retest invalidated" ||
    text === "No retest" ||
    text === "No rejection" ||
    text === "No break"
  ) {
    return [text];
  }
  const words = text.split(/\s+/).filter(Boolean);
  return words.length ? words : ["Pattern"];
}

type BarTimeIndex = { times: number[]; slot: number; ascending: boolean };

/** Per bars array: times, typical slot and order — worked out once, not per highlight. */
const barTimeIndexCache = new WeakMap<object, BarTimeIndex>();

function barTimeIndex(bars: readonly { time: number }[]): BarTimeIndex {
  const cached = barTimeIndexCache.get(bars);
  if (cached && cached.times.length === bars.length) return cached;
  const times: number[] = new Array(bars.length);
  const deltas: number[] = [];
  let ascending = true;
  for (let i = 0; i < bars.length; i += 1) {
    times[i] = asBarTimeMs(bars[i]!.time);
    if (i > 0) {
      const delta = times[i]! - times[i - 1]!;
      if (delta > 0) deltas.push(delta);
      else ascending = false;
    }
  }
  deltas.sort((a, b) => a - b);
  const slot = deltas.length ? deltas[Math.floor(deltas.length / 2)]! : 60_000;
  const built = { times, slot, ascending };
  barTimeIndexCache.set(bars, built);
  return built;
}

/**
 * Typical slot width from the series itself. Session gaps (RTH equity 1h →
 * next open is ~19h) must not become the column width or the tint covers
 * the whole day.
 */
export function typicalBarDurationMs(bars: readonly { time: number }[]): number {
  return barTimeIndex(bars).slot;
}

/**
 * Snap to the nearest open. Floor-to-previous would paint the engulfed bar
 * when `bar_time` is 1ms shy of the confirmation open.
 */
export function containingBarIndex(
  bars: readonly { time: number }[],
  timeMs: number
): number | null {
  if (!bars.length || !Number.isFinite(timeMs)) return null;
  const target = asBarTimeMs(timeMs);
  const { times, slot, ascending } = barTimeIndex(bars);
  const maxDist = Math.max(slot / 2, 1);
  let best: number | null = null;
  let bestDist = Infinity;
  if (ascending) {
    // First bar at or after the target; the nearest is that one or the one before (ties → earlier).
    let lo = 0;
    let hi = times.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (times[mid]! < target) lo = mid + 1;
      else hi = mid;
    }
    for (const i of [lo - 1, lo]) {
      if (i < 0 || i >= times.length) continue;
      const dist = Math.abs(times[i]! - target);
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    }
  } else {
    for (let i = 0; i < times.length; i += 1) {
      const dist = Math.abs(times[i]! - target);
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    }
  }
  if (best == null || bestDist > maxDist) return null;
  return best;
}

function barDurationMs(bars: readonly { time: number }[]): number {
  return Math.max(60_000, typicalBarDurationMs(bars));
}

/**
 * Visual column for a candle. Vela (like TradingView) centers each bar on
 * `bar.time`, so a bgcolor from open→next-open cuts through both wicks.
 * Shift the band by half a slot so it sits on the candle, not between them.
 */
export function barColumnSpan(
  bars: readonly { time: number }[],
  index: number
): { from: number; to: number } | null {
  const bar = bars[index];
  if (!bar) return null;
  const time = asBarTimeMs(bar.time);
  const half = barDurationMs(bars) / 2;
  return { from: time - half, to: time + half };
}

function emptyEmit(): {
  series: SeriesSpec[];
  backgrounds: Background[];
  labels: DrawingLabel[];
} {
  return { series: [], backgrounds: [], labels: [] };
}

function shapeLabel(opts: {
  id: string;
  timeMs: number;
  price: number;
  style: DrawingLabel["style"];
  color: string;
}): DrawingLabel {
  return {
    id: opts.id,
    paneId: "price",
    xloc: "bar_time",
    x: opts.timeMs,
    y: opts.price,
    yloc: "price",
    text: "",
    style: opts.style,
    color: opts.color,
    textColor: opts.color,
    size: "small",
    textAlign: "center",
    fontFamily: "default",
    overlay: true,
  };
}

function textLabel(opts: {
  id: string;
  timeMs: number;
  price: number;
  text: string;
  color: string;
}): DrawingLabel {
  return {
    id: opts.id,
    paneId: "price",
    xloc: "bar_time",
    x: opts.timeMs,
    y: opts.price,
    yloc: "price",
    text: opts.text,
    style: "none",
    color: opts.color,
    textColor: opts.color,
    size: "small",
    textAlign: "center",
    fontFamily: "default",
    overlay: true,
    noFill: true,
  };
}

/**
 * Tint the pattern column and label it. Do not recolor the candle — the wash
 * is the highlight; a second green body hides OHLC and can look like a ghost bar.
 */
export function emitPatternBarHighlight(
  highlight: PatternBarHighlight | null | undefined,
  bars: readonly OHLCV[],
  idSuffix = "",
  opts?: { lineOffset?: number; omitArrow?: boolean; omitBackground?: boolean }
): {
  series: SeriesSpec[];
  backgrounds: Background[];
  labels: DrawingLabel[];
} {
  if (!highlight || !Number.isFinite(highlight.timeMs) || !bars.length) {
    return emptyEmit();
  }
  const index = containingBarIndex(bars, highlight.timeMs);
  if (index == null) return emptyEmit();
  const bar = bars[index]!;
  const span = barColumnSpan(bars, index);
  if (!span) return emptyEmit();
  const timeMs = asBarTimeMs(bar.time);
  const suffix = idSuffix ? `-${idSuffix}` : "";

  const palette = patternBarPalette(highlight.direction, highlight.emphasis ?? "normal");
  const showArrow =
    opts?.omitArrow === true
      ? false
      : (highlight.showArrow ?? highlight.showText !== false);
  const showText = highlight.showText !== false;
  const showWash = highlight.showWash !== false;
  const backgrounds: Background[] =
    opts?.omitBackground === true || !showWash
      ? []
      : [
          {
            id: `tessera-pattern-bar-bg${suffix}`,
            paneId: "price",
            from: span.from,
            to: span.to,
            color: palette.fill,
            overlay: true,
          },
        ];

  const range = Math.abs(bar.high - bar.low);
  const pad = Math.max(range * 0.28, Math.abs(bar.close) * 0.00035, 1e-6);
  const below = highlight.direction === "short";
  const arrowStyle: DrawingLabel["style"] =
    highlight.direction === "short"
      ? "arrowdown"
      : highlight.direction === "long"
        ? "arrowup"
        : "diamond";
  const arrowY = below ? bar.low - pad : bar.high + pad;
  const linePad = pad * 1.15;
  const lines = showText ? stackPatternLabelLines(highlight.label) : [];
  const placed = below ? lines : [...lines].reverse();
  const lineOffset = Math.max(0, opts?.lineOffset ?? 0);

  const labels: DrawingLabel[] = [];
  if (showArrow) {
    labels.push(
      shapeLabel({
        id: `tessera-pattern-bar-shape${suffix}`,
        timeMs,
        price: arrowY,
        style: arrowStyle,
        color: palette.solid,
      })
    );
  }
  let textY = below ? arrowY - pad : arrowY + pad;
  textY += (below ? -linePad : linePad) * lineOffset;
  placed.forEach((line, i) => {
    labels.push(
      textLabel({
        id: `tessera-pattern-bar-text-${i}${suffix}`,
        timeMs,
        price: textY,
        text: line,
        color: palette.solid,
      })
    );
    textY += below ? -linePad : linePad;
  });

  return { series: [], backgrounds, labels };
}

function pathKindOrder(kind: PatternBarHighlight["kind"]): number {
  if (kind === "setup") return 1;
  if (kind === "break") return 2;
  if (kind === "rejection") return 3;
  if (kind === "retest") return 4;
  if (kind === "location_armed") return 5;
  if (kind === "respect" || kind === "reject" || kind === "break_retest") return 6;
  if (kind === "entry") return 7;
  if (kind === "invalid") return 8;
  return 0;
}

/** Draw multiple path / pattern markers (setup→break→retest→entry). Same-bar labels destack. */
export function emitPatternBarHighlights(
  highlights: readonly PatternBarHighlight[],
  bars: readonly OHLCV[]
): {
  series: SeriesSpec[];
  backgrounds: Background[];
  labels: DrawingLabel[];
} {
  if (!highlights.length || !bars.length) return emptyEmit();
  const groups = new Map<number, PatternBarHighlight[]>();
  highlights.forEach((highlight) => {
    if (!Number.isFinite(highlight.timeMs)) return;
    const index = containingBarIndex(bars, highlight.timeMs);
    if (index == null) return;
    const list = groups.get(index) ?? [];
    list.push(highlight);
    groups.set(index, list);
  });

  const series: SeriesSpec[] = [];
  const backgrounds: Background[] = [];
  const labels: DrawingLabel[] = [];
  let suffix = 0;
  Array.from(groups.values()).forEach((group) => {
    const sorted = group.slice().sort((a, b) => pathKindOrder(a.kind) - pathKindOrder(b.kind));
    let arrowIndex = sorted.findIndex(
      (h) => (h.showArrow ?? h.showText !== false) && h.kind !== "marker"
    );
    if (arrowIndex < 0) {
      arrowIndex = sorted.findIndex((h) => h.showArrow ?? h.showText !== false);
    }
    let lineOffset = 0;
    sorted.forEach((highlight, i) => {
      const part = emitPatternBarHighlight(highlight, bars, String(suffix), {
        omitArrow: i !== arrowIndex,
        lineOffset: highlight.showText === false ? 0 : lineOffset,
      });
      suffix += 1;
      if (highlight.showText !== false) {
        lineOffset += stackPatternLabelLines(highlight.label).length;
      }
      series.push(...part.series);
      backgrounds.push(...part.backgrounds);
      labels.push(...part.labels);
    });
  });
  return { series, backgrounds, labels };
}
